import { useQuery } from '@tanstack/vue-query'
import type { Notification as UINotification, User } from '~/types'

interface DBNotification {
  id: string
  userId: string
  bookingId: string
  title?: string
  content?: string
  readAt?: string | null
  createdAt?: string | null
  booking?: {
    id: string
    name?: string | null
    createdBy?: Partial<User> | null
  }
}

export const useNotification = () => {
  const api = useApi()

  const params = ref({
    unread: false,
    page: 1,
    limit: 25,
  })

  const { data: list, isLoading: loading } = useQuery({
    queryKey: ['notifications:list', params.value],
    queryFn: async () => {
      const n = await api.notifications.find({
        unread: params.value.unread,
        $page: params.value.page,
        $limit: params.value.limit,
      })

      return n.data
    },
    refetchInterval: 5000, 
    placeholderData: [],
  })

  const unreadCount = computed(() => list.value.filter((n) => !n.readAt).length)

  const markAsRead = async (id: string) => {
    await api.notifications.markRead(id)
    const item = list.value.find((n) => n.id === id)
    if (item) item.readAt = new Date().toISOString()
  }

  const markAllAsRead = async () => {
    await api.notifications.markAllRead()
    list.value = list.value.map((n) => ({
      ...n,
      readAt: new Date().toISOString(),
    }))
  }

  const markByBookingAsRead = async (bookingId: string) => {
    await api.notifications.markReadByBooking(bookingId)
    list.value = list.value.map((n) =>
      n.bookingId === bookingId
        ? { ...n, readAt: new Date().toISOString() }
        : n,
    )
  }

  const items = computed<UINotification[]>(
    () =>
      list.value.map((n) => {
        // Prefer customer info as sender, fallback to createdBy or System
        const customer = (n.booking as any)?.customer
        const createdBy = n.booking?.createdBy

        let senderName = 'System'
        let senderEmail = undefined
        let senderId = ''

        if (customer?.name) {
          senderName = customer.name
          senderEmail = customer.email
          senderId = customer.id || ''
        } else if (createdBy?.name) {
          senderName = createdBy.name
          senderEmail = (createdBy as any)?.email
          senderId = createdBy.id || ''
        }

        return {
          id: Number.isNaN(Number(n.id))
            ? list.value.indexOf(n) + 1
            : (n.id as unknown as number),
          unread: !n.readAt,
          title: n.title || 'New notification',
          sender: {
            id: senderId,
            name: senderName,
            email: senderEmail,
          } as any,
          body: n.content || n.title || 'New notification',
          date: n.createdAt || new Date().toISOString(),
        }
      }) ?? [],
  )

  const itemsWithMeta = computed(
    () =>
      list.value.map((n, idx) => ({
        ...(items.value[idx] as any),
        bookingId: n.bookingId,
        rawId: n.id,
      })) ?? [],
  )

  return {
    notifications: list,
    items,
    itemsWithMeta,
    loading,
    unreadCount,    
    markAsRead,
    markAllAsRead,
    markByBookingAsRead,
  }
}
